import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import type { InvoicePrefSubscriber } from "./invoicePrefs";

/**
 * Disable a subscriber from the console -- radaccess
 * POST /access/operator/{ns}/subscribers/{AccountID}/disable (owner
 * 2026-10-14: PHP, the only other way, is being retired). RBAC resource
 * `subscriberdisable` (superadmin and Operator); raduser and radcustomer
 * decide again on the same token.
 *
 * Sign-in stops at once. Nothing is deleted: the records are kept 2 years
 * (owner rule 2026-10-10), the history says who disabled the subscriber and
 * when, and the same mobile can be registered again.
 */
export interface DisableResult {
  /** Accounts (the mobile and any devices) moved out of sign-in. */
  accounts_moved: number;
  /** Account ids whose registration is now disabled. */
  registrations_disabled: string[];
}

const path = (ns: string, accountId: string) =>
  `/operator/${encodeURIComponent(ns)}/subscribers/${encodeURIComponent(accountId)}/disable`;

export async function disableSubscriber(identity: CallerIdentity, ns: string, accountId: string): Promise<DisableResult> {
  const r = await callGateway<DisableResult | null>(identity, config().gatewayBase, path(ns, accountId), { method: "POST" });
  return {
    accounts_moved: r.data?.accounts_moved ?? 0,
    registrations_disabled: r.data?.registrations_disabled ?? [],
  };
}

/** The registration says disable. A list from an older radaccess has no status: treat as enabled. */
export function isDisabled(s: Pick<InvoicePrefSubscriber, "status">): boolean {
  return (s.status ?? "").toLowerCase() === "disable";
}

/** One line for the result view. */
export function disableSummary(r: DisableResult): string {
  const moved = r.accounts_moved === 1 ? "1 sign-in account" : `${r.accounts_moved} sign-in accounts`;
  return r.accounts_moved > 0
    ? `Disabled: ${moved} stopped, records kept.`
    : "Disabled: the registration is marked disabled, records kept.";
}
